import type { NiiSheetWorkbookRows, SheetTable } from './buildNiiSheetRows'

/**
 * Which sheets and columns an NII spreadsheet export includes.
 *
 * Stored as **exclusions** rather than inclusions. Columns are discovered from
 * the data, so a workflow that gains a field would silently go missing from
 * every saved exploration if the selection listed what to include. Recording
 * what was turned off means new fields appear by default and only deliberate
 * choices persist.
 */

export type NiiSheetKey = 'tickets' | 'zones' | 'history'

export const NII_SHEET_KEYS: ReadonlyArray<NiiSheetKey> = [
  'tickets',
  'zones',
  'history',
]

export const NII_SHEET_LABELS: Record<NiiSheetKey, string> = {
  tickets: 'Tickets',
  zones: 'Zones',
  history: 'Inspection History',
}

export const NII_SHEET_DESCRIPTIONS: Record<NiiSheetKey, string> = {
  tickets: 'One row per asset',
  zones: 'One row per zone',
  history: 'One row per inspection record',
}

/**
 * Columns that stay even when everything else is deselected: the keys a row
 * is identified and joined by. Without them a row can't be traced back to its
 * ticket or zone, which would make the sheet unusable rather than just
 * narrower. Excluding the whole sheet is the way to drop them.
 */
export const NII_SHEET_REQUIRED_COLUMNS: Record<
  NiiSheetKey,
  ReadonlyArray<string>
> = {
  tickets: ['Ticket ID'],
  zones: ['Ticket ID', 'Zone #'],
  history: ['Ticket ID'],
}

export interface NiiSheetSelection {
  excludedSheets: ReadonlyArray<NiiSheetKey>
  excludedColumns: Partial<Record<NiiSheetKey, ReadonlyArray<string>>>
}

/** Everything included — the state a freshly added panel is in. */
export const DEFAULT_NII_SHEET_SELECTION: NiiSheetSelection = {
  excludedSheets: [],
  excludedColumns: {},
}

/** The column names available per sheet, as produced by `buildNiiSheetRows`. */
export type NiiSheetColumns = Record<NiiSheetKey, ReadonlyArray<string>>

export function isNiiSheetColumnRequired(
  sheet: NiiSheetKey,
  column: string
): boolean {
  return NII_SHEET_REQUIRED_COLUMNS[sheet].includes(column)
}

export function isNiiSheetIncluded(
  sheet: NiiSheetKey,
  selection: NiiSheetSelection
): boolean {
  return !selection.excludedSheets.includes(sheet)
}

/**
 * The columns of `headers` currently checked for a sheet. Required columns
 * always count as selected, so deselecting everything still leaves the keys.
 */
export function selectedNiiSheetColumns(
  sheet: NiiSheetKey,
  headers: ReadonlyArray<string>,
  selection: NiiSheetSelection
): ReadonlyArray<string> {
  const excluded = new Set(selection.excludedColumns[sheet] ?? [])
  return headers.filter(
    (header) => isNiiSheetColumnRequired(sheet, header) || !excluded.has(header)
  )
}

/**
 * Turns a set of checked columns back into exclusions, which is what gets
 * stored. Required columns are never recorded as excluded.
 */
export function excludedFromSelectedColumns(
  sheet: NiiSheetKey,
  headers: ReadonlyArray<string>,
  selectedColumns: ReadonlyArray<string>
): ReadonlyArray<string> {
  const selected = new Set(selectedColumns)
  return headers.filter(
    (header) =>
      !selected.has(header) && !isNiiSheetColumnRequired(sheet, header)
  )
}

const EMPTY_SHEET: SheetTable = { headers: [], rows: [] }

function applyToSheet(
  sheet: NiiSheetKey,
  table: SheetTable,
  selection: NiiSheetSelection
): SheetTable {
  if (!isNiiSheetIncluded(sheet, selection)) return EMPTY_SHEET

  const excluded = new Set(selection.excludedColumns[sheet] ?? [])
  const kept = table.headers
    .map((header, index) => ({ header, index }))
    .filter(
      ({ header }) =>
        isNiiSheetColumnRequired(sheet, header) || !excluded.has(header)
    )

  if (kept.length === table.headers.length) return table

  return {
    headers: kept.map(({ header }) => header),
    rows: table.rows.map((row) => kept.map(({ index }) => row[index] ?? '')),
  }
}

/**
 * Narrows built rows to the chosen sheets and columns. An excluded sheet
 * becomes empty, and `buildNiiSheetWorkbook` leaves empty sheets out of the
 * workbook entirely.
 */
export function applyNiiSheetSelection(
  rows: NiiSheetWorkbookRows,
  selection: NiiSheetSelection
): NiiSheetWorkbookRows {
  return {
    tickets: applyToSheet('tickets', rows.tickets, selection),
    zones: applyToSheet('zones', rows.zones, selection),
    history: applyToSheet('history', rows.history, selection),
  }
}
